"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useBikeStations = useBikeStations;
var _colors = require("@mui/material/colors");
var _maplibreGl = require("maplibre-gl");
var _notistack = require("notistack");
var _react = _interopRequireWildcard(require("react"));
var _client = require("react-dom/client");
var _bikeStationPopup = _interopRequireDefault(require("../../components/map/bike-station-popup"));
var _models = require("../../models");
var _utils = require("../../utils");
var _layers = require("./layers");
var _popup = require("./popup");
var _routing = require("./routing");
var _source = require("./source");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
const sourceId = 'bike-stations';
const layerId = 'bike-stations';
const asMarkerMinZoom = 14.5;
function useBikeStations(map, theme, search, smallDevice, {
  onSearchUpdated
} = {}) {
  const [selectedBikeStation, selectBikeStation] = (0, _react.useState)(null);
  const initialized = (0, _react.useRef)(false);
  const {
    getGeoJSONSource,
    addGeoJSONSource,
    updateGeoJSONSource,
    clearGeoJSONSource
  } = (0, _source.useSource)(map, sourceId);
  const {
    addCircleLayer
  } = (0, _layers.useLayers)(map);
  const bikeStationsRef = (0, _react.useRef)([]);
  const selectedIdRef = (0, _react.useRef)(null);
  const markers = (0, _react.useRef)({});
  const tooltip = (0, _react.useRef)(null);
  const snackbar = (0, _notistack.useSnackbar)();
  const {
    centerPopup
  } = (0, _popup.usePopup)(map);
  function init() {
    if (!map || initialized.current) return;
    if (getGeoJSONSource()) {
      initialized.current = true;
      return;
    }
    tooltip.current = new _maplibreGl.Popup({
      className: 'map-tooltip',
      closeButton: false
    });
    addGeoJSONSource();
    addCircleLayer(layerId, sourceId, {
      'circle-radius': 4,
      'circle-stroke-width': 2,
      'circle-stroke-opacity': 1,
      'circle-color': ['get', 'color'],
      'circle-stroke-color': '#fff'
    });
    map.on('mouseenter', layerId, event => {
      if (event.defaultPrevented) return;
      event.originalEvent.preventDefault();
      event.preventDefault();
      map.getCanvas().style.cursor = 'pointer';
      const {
        point,
        features
      } = event;
      if (map.queryRenderedFeatures(point).find(({
        layer: {
          id: layerId
        }
      }) => [_routing.wayPointsLayerId, _routing.arrivalLayerId].indexOf(layerId) !== -1) || !features || !features[0]) return;
      const {
        id,
        geometry,
        properties
      } = features[0];
      if (selectedIdRef.current === id) return;
      if (typeof id === 'number' && geometry.type === 'Point') {
        const {
          coordinates: [lng, lat]
        } = geometry;
        openTooltip(id, {
          lat,
          lng
        }, properties?.title || null, 6);
      }
    });
    map.on('click', layerId, event => {
      if (event.originalEvent.defaultPrevented) return;
      event.originalEvent.preventDefault();
      const {
        features
      } = event;
      if (features && features.length > 0) {
        const {
          id: bikeStationId,
          geometry
        } = features[0];
        if (typeof bikeStationId === 'number') {
          select(bikeStationsRef.current.find(({
            id
          }) => id === bikeStationId) || null);
        }
        if (geometry.type === 'Point') {
          const {
            coordinates: [lng, lat]
          } = geometry;
          map.flyTo({
            center: {
              lat,
              lng
            },
            zoom: asMarkerMinZoom
          });
        }
      }
    });
    map.on('mouseleave', layerId, () => clearTooltip());
    initialized.current = true;
  }
  function openTooltip(id, location, title, _offset = 0) {
    clearTooltip();
    if (!map || !title || selectedIdRef.current === id) return;
    const {
      anchor,
      offset
    } = (0, _utils.getAnchorAndOffset)(map, location, _offset);
    tooltip.current = new _maplibreGl.Popup({
      anchor,
      offset,
      className: 'map-tooltip',
      closeButton: false
    }).setHTML(title).setLngLat(location).addTo(map);
  }
  function addMarker(bikeStation) {
    if (!map) return;
    const {
      id,
      location: {
        point: {
          coordinates: [lng, lat]
        }
      },
      label,
      availableBikesPercentage,
      status
    } = bikeStation;
    const popup = new _maplibreGl.Popup({
      maxWidth: '400px',
      closeButton: false,
      closeOnClick: true,
      className: 'map-custom-popup',
      offset: 12
    }).setHTML(`<div id="bike-station-${id}-popup"></div>`);
    popup.on('close', () => {
      if (selectedIdRef.current === id && markers.current[id]) {
        unselect();
      }
    });
    const marker = (0, _utils.createImageMarker)({
      id: `bike-station-marker-${id}`,
      radius: 14,
      borderColor: _colors.grey[500],
      borderWidth: 1,
      backgroundColor: '#fff',
      backgroundImage: (status === 'closed' ? _models.bikeStationIcons.closed : _models.bikeStationIcons[availableBikesPercentage]) || '',
      backgroundSize: 28,
      zIndex: selectedIdRef.current === id ? '2' : '1'
    }, {
      onClick: () => {
        clearTooltip();
        if (selectedIdRef.current !== id) {
          selectedIdRef.current = id;
          closePrevPopup();
          selectBikeStation(bikeStation);
          openPopup(bikeStation);
        }
      },
      onMouseEnter: () => openTooltip(id, {
        lat,
        lng
      }, label, 15),
      onMouseLeave: () => clearTooltip()
    }).setLngLat({
      lat,
      lng
    }).setPopup(popup).addTo(map);
    markers.current[id] = {
      id,
      marker
    };
  }
  function openPopup(bikeStation) {
    closePrevPopup();
    if (smallDevice) return;
    const {
      id,
      location: {
        point: {
          coordinates: [lng, lat]
        }
      }
    } = bikeStation;
    const selectedMarker = selectedIdRef.current && markers.current[selectedIdRef.current];
    if (!selectedMarker) {
      unselect();
      return;
    }
    if (selectedMarker.marker.getPopup().isOpen()) return;
    selectedMarker.marker.togglePopup();
    const ele = document.getElementById(`bike-station-${id}-popup`);
    if (ele) {
      const container = (0, _client.createRoot)(ele);
      container.render(/*#__PURE__*/_react.default.createElement(_bikeStationPopup.default, {
        bikeStation: bikeStation,
        enqueueSnackbar: snackbar?.enqueueSnackbar,
        onClose: () => {
          if (selectedMarker.marker.getPopup().isOpen()) {
            selectedMarker.marker.togglePopup();
            if (container) container.unmount();
          }
        },
        onSearchUpdated: onSearchUpdated,
        onUpdated: ({
          id,
          status,
          availableBikesPercentage
        }) => {
          const ele = document.getElementById(`bike-station-marker-${id}`);
          if (ele) {
            ele.style.backgroundImage = `url(${status === 'closed' ? _models.bikeStationIcons.closed : _models.bikeStationIcons[availableBikesPercentage]})`;
          }
        },
        search: search,
        theme: theme
      }));
    }
    centerPopup({
      lat,
      lng
    }, selectedMarker.marker.getPopup(), {
      height: 400
    });
  }
  function closePrevPopup() {
    Object.values(markers.current).forEach(({
      id,
      marker
    }) => {
      if (id !== selectedIdRef.current && marker.getPopup().isOpen()) marker.togglePopup();
    });
  }
  function select(bikeStation) {
    selectedIdRef.current = bikeStation?.id || null;
    selectBikeStation(bikeStation);
    if (!bikeStation || !map) return;
    const {
      location: {
        point: {
          coordinates: [lng, lat]
        }
      }
    } = bikeStation;
    map.flyTo({
      center: {
        lat,
        lng
      },
      zoom: asMarkerMinZoom
    });
  }
  function unselect() {
    selectedIdRef.current = null;
    selectBikeStation(null);
    closePrevPopup();
  }
  function update(bikeStations, {
    forceAsMarkers
  } = {}) {
    bikeStationsRef.current = bikeStations;
    if (!map) return;
    const zoom = map.getZoom();
    if (zoom >= asMarkerMinZoom || forceAsMarkers) {
      clearGeoJSONSource();
      const bikeStationsIds = bikeStations.reduce((res, {
        id
      }) => {
        res[id] = true;
        return res;
      }, {});

      // remove markers that are no longer visible
      Object.values(markers.current).filter(({
        id
      }) => !bikeStationsIds[id]).forEach(({
        id,
        marker
      }) => {
        marker.remove();
        delete markers.current[id];
      });

      // unselect bike station if no longer visible
      if (selectedIdRef.current && !markers.current[selectedIdRef.current]) {
        unselect();
      }

      // add markers that were not visible
      bikeStations.filter(({
        id
      }) => !markers.current[id]).forEach(bikeStation => addMarker(bikeStation));

      // close previous selected bike station popup
      closePrevPopup();

      // open selected bike station popup
      const selectedBikeStation = selectedIdRef.current && bikeStations.find(({
        id
      }) => selectedIdRef.current === id);
      if (selectedBikeStation) openPopup(selectedBikeStation);
    } else {
      clearMarkers();
      unselect();
      updateGeoJSONSource({
        type: 'FeatureCollection',
        features: bikeStations.map(({
          id,
          label,
          location: {
            point
          },
          color
        }) => {
          return {
            id,
            type: 'Feature',
            geometry: point,
            properties: {
              title: label,
              color
            }
          };
        })
      });
    }
  }
  function clearTooltip() {
    if (map) map.getCanvas().style.cursor = '';
    tooltip.current?.remove();
  }
  function clearMarkers(keepSelected = false) {
    Object.values(markers.current).forEach(({
      id,
      marker
    }) => {
      if (!keepSelected || id !== selectedIdRef.current) marker.remove();
    });
    const marker = selectedIdRef.current && markers.current[selectedIdRef.current];
    if (keepSelected && selectedIdRef.current && marker) markers.current = {
      [selectedIdRef.current]: marker
    };else markers.current = {};
  }
  function clear(keepSelected = false) {
    bikeStationsRef.current = bikeStationsRef.current.filter(({
      id
    }) => keepSelected && id === selectedIdRef.current);
    clearMarkers(keepSelected);
    clearGeoJSONSource();
    if (!keepSelected) unselect();
    clearTooltip();
  }
  return {
    initialized: initialized.current,
    selectedBikeStation,
    init,
    update,
    unselect,
    clear
  };
}